import type { SupabaseClient } from "@supabase/supabase-js";
import { saveStaffReturnPath } from "./ehrReturnPath";
import { withTimeout } from "./asyncUtils";

export const SESSION_EXPIRED_STORAGE_KEY = "biocure_session_expired_reason";

export type SessionExpiredReason =
  | "inactivity"
  | "timeout"
  | "replaced"
  | "invalid";

const SESSION_EXPIRED_MESSAGES: Record<SessionExpiredReason, string> = {
  inactivity:
    "Your session ended due to inactivity. Please sign in again to continue.",
  timeout: "Your session has expired. Please sign in again.",
  replaced:
    "You were signed out because your account was used to sign in on another device.",
  invalid: "Your session is no longer valid. Please sign in again.",
};

/** Persists a one-time message shown on the sign-in page after forced logout. */
export function markSessionExpired(
  reason: SessionExpiredReason = "invalid",
): void {
  try {
    if (!sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY)) {
      sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, reason);
    }
  } catch {
    // ignore
  }
}

export function consumeSessionExpiredMessage(): string | null {
  try {
    const reason = sessionStorage.getItem(
      SESSION_EXPIRED_STORAGE_KEY,
    ) as SessionExpiredReason | null;
    if (!reason) return null;
    sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    return SESSION_EXPIRED_MESSAGES[reason] ?? SESSION_EXPIRED_MESSAGES.invalid;
  } catch {
    return null;
  }
}

const SIGN_IN_PATH = "/signin";
const PUBLIC_AUTH_PATHS = new Set([
  SIGN_IN_PATH,
  "/password-reset",
  "/password-reset-otp",
  "/set-new-password",
  "/invite",
  "/invite-verify",
]);

/** Hard navigation fallback when React route guards cannot redirect (e.g. expired mid-session). */
export function redirectToSignInAfterExpiry(): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (PUBLIC_AUTH_PATHS.has(path)) return;
  saveStaffReturnPath();
  window.location.assign(SIGN_IN_PATH);
}

/** GoTrue / JWT errors that mean the server-side session is no longer valid. */
export function isSessionInvalidAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { message?: string; status?: number };
  const m = (err.message || "").toLowerCase();

  if (err.status === 401 || err.status === 403) {
    return (
      m.includes("session") ||
      m.includes("jwt") ||
      m.includes("token") ||
      m.includes("not authorized")
    );
  }

  return (
    m.includes("session_not_found") ||
    m.includes("session from session_id") ||
    m.includes("auth session missing") ||
    m.includes("invalid refresh token") ||
    m.includes("refresh token not found") ||
    m.includes("jwt expired")
  );
}

export type ApiRequestError = Error & { status?: number; code?: string };

export function isUnauthorizedApiError(e: unknown): boolean {
  if (e === null || typeof e !== "object") return false;
  const err = e as ApiRequestError;
  if (err.status === 401) return true;
  const m = (err.message || "").toLowerCase();
  return (
    m.includes("authorization required") ||
    m.includes("invalid jwt") ||
    m.includes("jwt expired") ||
    m.includes("token expired") ||
    m.includes("session_not_found") ||
    m.includes("auth session missing") ||
    m.includes("invalid authorization token")
  );
}

function reasonFromAuthError(error: unknown): SessionExpiredReason {
  const m =
    error && typeof error === "object" && "message" in error
      ? String((error as { message?: string }).message || "").toLowerCase()
      : "";
  if (m.includes("session_id") || m.includes("another")) return "replaced";
  if (m.includes("inactiv") || m.includes("idle")) return "inactivity";
  return "timeout";
}

const TOKEN_REFRESH_BUFFER_SEC = 60;
const GET_SESSION_TIMEOUT_MS = 4000;

let cachedAccessToken: string | null = null;
let cachedExpiresAt = 0;

/** Keep a memory copy of the session token so API calls still work if getSession hangs. */
export function rememberAccessToken(
  session: { access_token?: string; expires_at?: number } | null | undefined,
): void {
  if (!session?.access_token) {
    cachedAccessToken = null;
    cachedExpiresAt = 0;
    return;
  }
  cachedAccessToken = session.access_token;
  cachedExpiresAt = session.expires_at ?? 0;
}

function cachedTokenIfUsable(): string | null {
  if (!cachedAccessToken) return null;
  if (sessionExpiresWithin({ expires_at: cachedExpiresAt }, 0)) return null;
  return cachedAccessToken;
}

function sessionExpiresWithin(session: { expires_at?: number }, seconds: number): boolean {
  const expiresAt = session.expires_at ?? 0;
  if (!expiresAt) return false;
  return expiresAt - Math.floor(Date.now() / 1000) <= seconds;
}

/** Returns an access token, refreshing first when expired or near expiry. */
export async function getValidAccessToken(
  supabase: SupabaseClient,
): Promise<string | null> {
  try {
    const {
      data: { session },
    } = await withTimeout(
      supabase.auth.getSession(),
      GET_SESSION_TIMEOUT_MS,
      "Session token lookup timed out",
    );
    if (session?.access_token) {
      rememberAccessToken(session);
      if (!sessionExpiresWithin(session, TOKEN_REFRESH_BUFFER_SEC)) {
        return session.access_token;
      }
      const { data, error } = await withTimeout(
        supabase.auth.refreshSession(),
        GET_SESSION_TIMEOUT_MS,
        "Session refresh timed out",
      );
      if (error || !data.session) return cachedTokenIfUsable() ?? session.access_token;
      rememberAccessToken(data.session);
      return data.session.access_token;
    }
  } catch (e) {
    console.warn("[auth] getValidAccessToken using cached token:", e);
  }
  return cachedTokenIfUsable();
}

/** Attempts to renew the session after an expired access token or 401 from the API. */
export async function tryRefreshSession(
  supabase: SupabaseClient,
): Promise<boolean> {
  const { data, error } = await supabase.auth.refreshSession();
  return !error && !!data.session;
}

const VALIDATION_INTERVAL_MS = 60_000;

/**
 * Validates the session with Supabase when the tab is focused and periodically
 * while visible. Supabase enforces inactivity / time-box limits on refresh;
 * this detects expiry promptly when the user returns to the app.
 */
export function installSessionWatchdog(
  supabase: SupabaseClient,
  onInvalid: (reason: SessionExpiredReason) => void,
): () => void {
  let validating = false;

  const invalidateSession = async (error: unknown) => {
    onInvalid(reasonFromAuthError(error));
    await supabase.auth.signOut();
    redirectToSignInAfterExpiry();
  };

  const validate = async () => {
    if (validating || document.visibilityState !== "visible") return;
    validating = true;
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;

      if (sessionExpiresWithin(session, TOKEN_REFRESH_BUFFER_SEC)) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError && isSessionInvalidAuthError(refreshError)) {
          await invalidateSession(refreshError);
          return;
        }
      }

      const { error } = await supabase.auth.getUser();
      if (error && isSessionInvalidAuthError(error)) {
        const recovered = await tryRefreshSession(supabase);
        if (recovered) {
          const { error: retryError } = await supabase.auth.getUser();
          if (!retryError || !isSessionInvalidAuthError(retryError)) return;
        }
        await invalidateSession(error);
      }
    } finally {
      validating = false;
    }
  };

  const onVisible = () => {
    if (document.visibilityState === "visible") void validate();
  };

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
  const intervalId = window.setInterval(
    () => void validate(),
    VALIDATION_INTERVAL_MS,
  );

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", onVisible);
    window.clearInterval(intervalId);
  };
}

let axiosGuardRegistered = false;

/** Signs the user out when any axios call returns 401 with an active local session. */
export function registerAxiosSessionGuard(): void {
  if (axiosGuardRegistered || typeof window === "undefined") return;
  axiosGuardRegistered = true;

  void import("axios").then(({ default: axios }) => {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 401) {
          const { supabase } = await import("../supabaseClient");
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            const config = error.config as
              | (typeof error.config & { __sessionRetried?: boolean })
              | undefined;
            if (config && !config.__sessionRetried) {
              const recovered = await tryRefreshSession(supabase);
              if (recovered) {
                config.__sessionRetried = true;
                const token = await getValidAccessToken(supabase);
                if (token) {
                  config.headers = config.headers ?? {};
                  config.headers.Authorization = `Bearer ${token}`;
                  return axios.request(config);
                }
              }
            }
            markSessionExpired("invalid");
            saveStaffReturnPath();
            await supabase.auth.signOut();
            redirectToSignInAfterExpiry();
          }
        }
        return Promise.reject(error);
      },
    );
  });
}
